// ── "Receiving" detection for the studies worklist ──────────────────────────
//
// While a modality is still archiving into a study, that study's row counts
// climb on each worklist poll (the list query already refetches on
// STUDIES_REFETCH_INTERVAL_MS). Before this, the numbers just ticked up
// silently, so a radiologist could open a half-arrived study without any
// signal that more was coming. This module turns successive list snapshots
// into "which studies are currently receiving" — no extra network, no extra
// query, purely derived from payloads the worklist already has.
//
// WHY COUNT DELTAS AND NOT A FRESHNESS TIMESTAMP
// The obvious shortcut for a FIRST sighting would be "updatedAt is very
// recent, so it must still be arriving". That signal does not exist here, and
// it was checked rather than assumed:
//   - `StudyListItem` (types/index.ts) carries no updatedAt.
//   - The list projection (backend WebApi Controllers/Study/GetStudyList.cs)
//     selects no freshness column — only the counts, via the sc/ic CTEs.
//   - The `study` table itself has no updated_at at all; the only clock on the
//     row is `received_at TIMESTAMPTZ DEFAULT now()`, stamped once when the
//     row is created and never touched when images are appended (images land
//     in the `image` table, which does not write back to `study`).
// So a "recent" study is one that was CREATED recently, not one that is still
// growing — using it would badge every freshly-imported-but-complete study.
// Growth is therefore inferred from count deltas alone, which means the very
// first snapshot of a study can never mark it receiving (see below).
//
// FIRST SIGHTING
// A study with no previous snapshot is never marked receiving. Without that
// rule every row would badge on first page load, on every filter change and
// on every page change — noise that would train people to ignore the chip.
// The cost is one poll of latency (~25s) before a genuinely-arriving study
// gets its chip, which is the right trade.
//
// GRACE WINDOW
// A modality pauses between series, so consecutive polls can legitimately show
// no growth mid-acquisition. A chip that blinks off in those gaps is worse
// than no chip, so a study stays marked for RECEIVING_GRACE_MS after the last
// observed growth. `now` is a parameter, never Date.now(), so tests are
// deterministic.
/**
 * How long a study keeps its chip after the last observed activity.
 *
 * Was 90s, which was wrong in practice: a modality pauses between series for
 * longer than that, so the chip blinked off mid-study and readers stopped
 * trusting it. Then 30 minutes, which over-corrected: every finished study
 * kept its chip for half an hour. Now 10 minutes, matching the backend's
 * ReceivingSilenceWindow (GetStudyIngestStatus.cs) — MR sequences run 2–8
 * minutes and most scanners send series-by-series, so 10 minutes still
 * bridges real inter-series gaps.
 *
 * Note the viewer's chip is better off than this one: the server verdict it
 * polls (/study/{id}/ingest-status) also clears the moment MPPS reports the
 * acquisition complete. This tracker only sees list-payload counts, so for
 * the worklist the grace window is still the only end-of-study evidence.
 *
 * The cost of the window is bounded: with pendingImageCount available the
 * chip clears as soon as storage catches up, so this only governs the tail
 * after a sender goes away mid-transfer.
 */
export const RECEIVING_GRACE_MS = 10 * 60_000;
/**
 * Shared empty result. Returned (by identity) whenever nothing is receiving,
 * so the idle case allocates nothing and every consumer's `Object.is` bails.
 * Exported so a consumer can seed its state with the SAME object and skip the
 * otherwise-guaranteed re-render on mount.
 */
export const NO_STUDIES_RECEIVING = new Set();
const EMPTY_RECEIVING = NO_STUDIES_RECEIVING;
/** True when this dimension strictly increased. Unknown (null) never counts. */
function increased(prev, next) {
    return typeof prev === 'number' && typeof next === 'number' && next > prev;
}
/**
 * Growth = images OR series went UP. Strictly up, so a deleted series (counts
 * going down) is not mistaken for an arrival. The identity check is the common
 * case: React Query's structural sharing hands back the very same `counts`
 * object when a poll changed nothing, so an idle poll costs one comparison
 * per row and no allocation.
 */
function grew(prev, next) {
    if (prev === next)
        return false;
    return (increased(prev.imageCount, next.imageCount) || increased(prev.seriesCount, next.seriesCount));
}
function sameMembers(a, b) {
    if (a.size !== b.size)
        return false;
    for (const id of a)
        if (!b.has(id))
            return false;
    return true;
}
/**
 * Create a tracker. One per worklist instance; all state is per-instance and
 * bounded by the studies currently on the page (entries for studies that leave
 * the list are dropped, so a long session cannot leak).
 */
export function createReceivingTracker(graceMs = RECEIVING_GRACE_MS) {
    /** studyId -> the counts object from the last snapshot that contained it. */
    const lastCounts = new Map();
    /** studyId -> `now` at the last observed growth. Pruned as it expires. */
    const lastGrowthAt = new Map();
    /** Last returned set, reused by identity when membership is unchanged. */
    let published = EMPTY_RECEIVING;
    /**
     * Expire aged-out entries and return the receiving set, preserving the
     * previous set's identity when membership did not change. That referential
     * stability is what lets React bail out on a poll that changed nothing.
     */
    function publish(now) {
        for (const [id, at] of lastGrowthAt) {
            if (now - at >= graceMs)
                lastGrowthAt.delete(id);
        }
        if (lastGrowthAt.size === 0) {
            published = EMPTY_RECEIVING;
            return published;
        }
        const next = new Set(lastGrowthAt.keys());
        if (sameMembers(next, published))
            return published;
        published = next;
        return published;
    }
    return {
        observe(studies, now) {
            const seen = new Set();
            for (const study of studies) {
                seen.add(study.id);
                const prev = lastCounts.get(study.id);
                const pending = study.counts.pendingImageCount;
                if (typeof pending === 'number' && pending > 0) {
                    // Authoritative: the backend says storage still owes us bytes for
                    // this study. Mark it on the FIRST sighting — no baseline snapshot
                    // needed. This is what fixes "slow to appear": the old delta path
                    // could not badge a study until its second poll (~50s), because a
                    // single snapshot carries no evidence of change.
                    lastGrowthAt.set(study.id, now);
                }
                else if (prev && grew(prev, study.counts)) {
                    // Legacy fallback for a backend that does not send the pending count:
                    // infer arrival from counts going up between snapshots.
                    lastGrowthAt.set(study.id, now);
                }
                lastCounts.set(study.id, study.counts);
            }
            // Studies that left the page (filtered out, paged past, deleted) stop
            // being tracked, so both maps stay bounded by the current page.
            for (const id of lastCounts.keys())
                if (!seen.has(id))
                    lastCounts.delete(id);
            for (const id of lastGrowthAt.keys())
                if (!seen.has(id))
                    lastGrowthAt.delete(id);
            return publish(now);
        },
        refresh(now) {
            return publish(now);
        },
        settle(studyId, now) {
            lastCounts.delete(studyId);
            lastGrowthAt.delete(studyId);
            return publish(now);
        },
    };
}
